import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import LockIcon from '@mui/icons-material/Lock';
import SearchIcon from '@mui/icons-material/Search';
import PageHeader from '../components/common/PageHeader';
import api from '../services/api';
import { useAuth } from '../hooks/useAuth';
import { Rolle } from '../types';

interface MatrixRow {
  category: string;
  category_label: string;
  permission: string;
  action: string;
  action_label: string;
  description: string;
  roles: Record<string, boolean>;
}

interface MatrixData {
  roles: Rolle[];
  matrix: MatrixRow[];
  meta: {
    dynamic_from_route_checks: boolean;
    permission_count: number;
  };
}

const BerechtigungsmatrixPage: React.FC = () => {
  const { refreshUser } = useAuth();
  const [matrixData, setMatrixData] = useState<MatrixData | null>(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [savingRoleId, setSavingRoleId] = useState<number | null>(null);
  const [roleDialogOpen, setRoleDialogOpen] = useState(false);
  const [editingRole, setEditingRole] = useState<Rolle | null>(null);
  const [roleName, setRoleName] = useState('');

  const loadMatrix = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/rollen/berechtigungsmatrix');
      setMatrixData(res.data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadMatrix();
  }, [loadMatrix]);

  const rolePermissions = (roleId: number): string[] => {
    if (!matrixData) return [];
    return matrixData.matrix
      .filter((row) => Boolean(row.roles[String(roleId)]))
      .map((row) => row.permission);
  };

  const handleTogglePermission = async (role: Rolle, permission: string, checked: boolean) => {
    if (role.is_system) return;
    setSavingRoleId(role.id);
    const next = new Set(rolePermissions(role.id));
    if (checked) {
      next.add(permission);
    } else {
      next.delete(permission);
    }
    try {
      await api.put(`/rollen/${role.id}/permissions`, { permissions: Array.from(next) });
      await loadMatrix();
      await refreshUser();
    } finally {
      setSavingRoleId(null);
    }
  };

  const openCreateDialog = () => {
    setEditingRole(null);
    setRoleName('');
    setRoleDialogOpen(true);
  };

  const openEditDialog = (role: Rolle) => {
    setEditingRole(role);
    setRoleName(role.name);
    setRoleDialogOpen(true);
  };

  const saveRole = async () => {
    const name = roleName.trim();
    if (!name) return;
    if (editingRole) {
      await api.patch(`/rollen/${editingRole.id}`, { name });
    } else {
      await api.post('/rollen', { name });
    }
    setRoleDialogOpen(false);
    loadMatrix();
  };

  const deleteRole = async (role: Rolle) => {
    if (role.is_system) return;
    if (!window.confirm(`Rolle "${role.name}" wirklich löschen?`)) return;
    await api.delete(`/rollen/${role.id}`);
    loadMatrix();
    await refreshUser();
  };

  return (
    <Box>
      <PageHeader
        title="Berechtigungsmatrix"
        subtitle="Rollen verwalten und Berechtigungen dynamisch zuweisen"
        breadcrumbs={[{ label: 'Admin' }, { label: 'Berechtigungsmatrix' }]}
      />

      <Card>
        <CardContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            Berechtigungen werden serverseitig aus der Datenbank gelesen. Die Admin-Rolle ist
            geschützt und hat immer alle Berechtigungen.
          </Alert>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }} alignItems={{ md: 'center' }}>
            <TextField
              placeholder="Berechtigung suchen..."
              size="small"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              sx={{ width: 300 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
            <Button variant="contained" startIcon={<AddIcon />} onClick={openCreateDialog}>
              Rolle hinzufügen
            </Button>
          </Stack>
          {loading ? (
            <Box sx={{ py: 2 }}>
              {Array.from({ length: 8 }).map((_, i) => (
                <Skeleton key={i} variant="rounded" height={40} sx={{ mb: 1 }} />
              ))}
            </Box>
          ) : matrixData ? (
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, minWidth: 250 }}>Berechtigung</TableCell>
                    {matrixData.roles.map((role) => (
                      <TableCell key={role.id} align="center" sx={{ fontWeight: 700, minWidth: 140 }}>
                        <Stack spacing={0.5} alignItems="center">
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            {role.is_system && <LockIcon sx={{ fontSize: 16, color: 'text.secondary' }} />}
                            <span>{role.name}</span>
                          </Stack>
                          {!role.is_system && (
                            <Stack direction="row" spacing={0.5}>
                              <Tooltip title="Rolle umbenennen">
                                <IconButton size="small" onClick={() => openEditDialog(role)}>
                                  <EditIcon fontSize="inherit" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Rolle löschen">
                                <IconButton size="small" color="error" onClick={() => deleteRole(role)}>
                                  <DeleteIcon fontSize="inherit" />
                                </IconButton>
                              </Tooltip>
                            </Stack>
                          )}
                        </Stack>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(() => {
                    const q = filter.toLowerCase();
                    const filtered = matrixData.matrix.filter((row) =>
                      !q ||
                      row.category_label.toLowerCase().includes(q) ||
                      row.action_label.toLowerCase().includes(q) ||
                      row.permission.toLowerCase().includes(q) ||
                      (row.description && row.description.toLowerCase().includes(q))
                    );
                    let lastCategory = '';
                    return filtered.map((row, idx) => {
                      const showHeader = row.category !== lastCategory;
                      lastCategory = row.category;
                      return (
                        <React.Fragment key={idx}>
                          {showHeader && (
                            <TableRow>
                              <TableCell
                                colSpan={matrixData.roles.length + 1}
                                sx={{ fontWeight: 700, bgcolor: 'action.hover', py: 1 }}
                              >
                                {row.category_label}
                              </TableCell>
                            </TableRow>
                          )}
                          <TableRow hover>
                            <TableCell sx={{ pl: 4 }}>
                              {row.action_label}
                              {row.description && (
                                <Typography variant="caption" display="block" color="text.disabled" sx={{ mt: 0.25, lineHeight: 1.3 }}>
                                  {row.description}
                                </Typography>
                              )}
                            </TableCell>
                            {matrixData.roles.map((role) => (
                              <TableCell key={role.id} align="center">
                                <Checkbox
                                  checked={Boolean(row.roles[String(role.id)])}
                                  disabled={role.is_system || savingRoleId === role.id}
                                  onChange={(event) => handleTogglePermission(role, row.permission, event.target.checked)}
                                  size="small"
                                />
                              </TableCell>
                            ))}
                          </TableRow>
                        </React.Fragment>
                      );
                    });
                  })()}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography color="text.secondary">Keine Daten verfügbar.</Typography>
          )}
        </CardContent>
      </Card>

      <Dialog open={roleDialogOpen} onClose={() => setRoleDialogOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>{editingRole ? 'Rolle umbenennen' : 'Rolle hinzufügen'}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Rollenname"
            margin="dense"
            value={roleName}
            onChange={(event) => setRoleName(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                saveRole();
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRoleDialogOpen(false)}>Abbrechen</Button>
          <Button variant="contained" onClick={saveRole} disabled={!roleName.trim()}>
            Speichern
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BerechtigungsmatrixPage;
